import styled from "styled-components";
import { _ } from "@sablier/v2-mixins";
import { ProgressBar } from "~/components/atoms";
import { Amount, Label } from "~/components/molecules";
import type { ThemeType } from "@sablier/v2-themes";
import type { ComponentProps } from "react";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    border-radius: 6px;
    background-color: ${(props) => props.theme.colors.dark300};
  }
`;

const Row = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    min-height: 22px;
    column-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
  }
`;

const Legend = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 140px;
    p {
      ${(props) => props.theme.styles.ellipsis}
    }
  }
`;

const Progress = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    flex: 1;
    & > * {
      width: 100%;
      color: ${(props) => props.theme.colors.gray400};
    }
  }
`;
const Total = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    min-width: 160px;
    column-gap: 8px;
  }
`;

const Percentage = styled.div`
  & > p {
    &:before {
      content: "(";
    }
    &:after {
      content: ")";
    }
    ${(props) => props.theme.styles.textElement}
    & {
      color: ${(props) => props.theme.colors.gray400};
    }
  }
`;

const Indicator = styled.div<{ color?: keyof ThemeType["colors"] }>`
  ${(props) => props.theme.styles.column}
  & {
    justify-content: flex-start;
    height: 100%;
    &:after {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: ${(props) =>
        props.theme.colors[props.color || "white"]};
    }
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme.colors.dark600};
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding: calc(${(props) => props.theme.sizes.edge} * 1);
    ${Progress} {
      display: none;
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    padding: calc(${(props) => props.theme.sizes.edge} * 1);

    ${Row} {
      flex-wrap: wrap;
      gap: 8px;
    }
    ${Legend} {
      width: auto;
    }
  }
`;

interface Props {
  className?: string;
  items: {
    label?: ComponentProps<typeof Label>;
    progress?: ComponentProps<typeof ProgressBar>;
    amount?: ComponentProps<typeof Amount>;
    percentage?: number;
    indicator?: keyof ThemeType["colors"];
    isDivider?: boolean;
    tooltip?: string;
  }[];
}

function Position({ className, items }: Props) {
  return (
    <Wrapper className={className}>
      {items.map((item, index) =>
        item.isDivider ? (
          <Divider key={`${item.label?.value || index}-${index}`} />
        ) : (
          <Row key={`${item.label?.value || index}-${index}`}>
            <Legend>
              <Label {...item.label} />
            </Legend>
            {item.progress && (
              <Progress>
                <ProgressBar {...item.progress} />
              </Progress>
            )}
            <Total>
              {item.amount ? <Amount {...item.amount} /> : false}
              {item.percentage ? (
                <Percentage>
                  <p>{_.toPercentage(item.percentage)}</p>
                </Percentage>
              ) : (
                false
              )}
              {item.indicator && <Indicator color={item.indicator} />}
            </Total>
          </Row>
        ),
      )}
    </Wrapper>
  );
}

export default Position;
